import { useQuery } from '@tanstack/react-query'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { reactQueryKeys } from 'reactQueryKeys/reactQueryKeys'
import { getWalletProfilesV2 } from 'services/CustomerService'
import { getSupportedCurrencies } from 'services/WalletsService'
import { CustomerWalletProfile } from 'types/customers'
import { CurrencyInterface } from 'types/wallets'

export const IGNORED_CURRENCIES = ['FDUSD']

const selectCurrenciesFromProfiles = (
  profileResponse: CustomerWalletProfile,
  cryptoOnly: boolean
): CurrencyInterface[] => {
  if (!profileResponse?.profiles) return []

  const currenciesMap = new Map<string, CurrencyInterface>()

  profileResponse.profiles.forEach(profile => {
    const isFiat = profile.id.startsWith('fiat:')

    profile.currencies.forEach(currencyCode => {
      if (!currenciesMap.has(currencyCode)) {
        currenciesMap.set(currencyCode, {
          id: currencyCode,
          code: currencyCode,
          name: currencyCode,
          fiat: isFiat,
        })
      }
    })
  })

  const currencies = Array.from(currenciesMap.values()).sort((a, b) => a.code.localeCompare(b.code))

  if (cryptoOnly) {
    return currencies?.filter(currency => !currency.fiat)
  }

  return currencies
}

const selectCurrenciesFromList = (
  currencyList: CurrencyInterface[],
  cryptoOnly: boolean
): CurrencyInterface[] => {
  const sorted = currencyList.sort((a, b) => a.code.localeCompare(b.code))

  if (cryptoOnly) {
    return sorted?.filter(currency => !currency.fiat)
  }

  return sorted
}

export const useSupportedCurrenciesQuery = (cryptoOnly = false) => {
  const { enableWalletCreationV2 } = useFeatureFlags()

  return useQuery({
    queryKey: enableWalletCreationV2
      ? reactQueryKeys.customerWalletProfiles()
      : reactQueryKeys.supportedCurrencies(),
    queryFn: async (): Promise<CustomerWalletProfile | CurrencyInterface[]> => {
      if (enableWalletCreationV2) {
        return getWalletProfilesV2()
      }
      return getSupportedCurrencies()
    },
    select: (response: CustomerWalletProfile | CurrencyInterface[]) => {
      if (enableWalletCreationV2) {
        return selectCurrenciesFromProfiles(response as CustomerWalletProfile, cryptoOnly)
      }

      return selectCurrenciesFromList(response as CurrencyInterface[], cryptoOnly)
    },
  })
}
